import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { UseFormReturn } from 'react-hook-form-v7';

import { UserDataSection } from '@personnel/presentation/components/form/EmployeeForm/UserDataSection';
import { ContactDataSection } from '@personnel/presentation/components/form/EmployeeForm/ContactDataSection';
import { PinSection } from '@personnel/presentation/components/form/EmployeeForm/PinSection';
import { AssignStoresSection } from '@personnel/presentation/components/form/EmployeeForm/AssignStoresSection';
import { InfoNote } from '@personnel/presentation/components/shared';
import { usePersonnelStores } from '@personnel/presentation/hooks';
import type { EmployeeFormData } from '@personnel/presentation/types';

interface EmployeeGeneralFormProps {
  form: UseFormReturn<EmployeeFormData>;
  isEditMode: boolean;
  /** True while the record's email is still unverified, which is when resending makes sense. */
  isAccountInactive: boolean;
}

/**
 * The general tab: four sibling cards, no accordion.
 *
 * Which of them accept input is decided by the role chosen in the first card. Both closing notes
 * live here and not in the footer, because both talk about the data this tab asks for.
 *
 * @param {EmployeeGeneralFormProps} props - Component properties
 * @returns {React.ReactElement} Employee general form section
 */
export const EmployeeGeneralForm = React.memo(function EmployeeGeneralForm({
  form,
  isEditMode,
  isAccountInactive,
}: EmployeeGeneralFormProps): React.ReactElement {
  const { t } = useTranslation('personnel-employees');

  const { data: stores = [] } = usePersonnelStores();
  const memoizedStores = useMemo(() => stores, [stores]);

  return (
    <>
      <UserDataSection form={form} />
      <ContactDataSection form={form} />
      <PinSection form={form} />
      <AssignStoresSection form={form} stores={memoizedStores} />
      {!isEditMode && (
        <InfoNote className="w-full">
          <Trans components={{ bold: <strong />, br: <br /> }} i18nKey="form.footer.noPasswordNote" t={t} />
        </InfoNote>
      )}
      {isEditMode && isAccountInactive && (
        <p className="text-sm text-muted-foreground">
          <Trans components={{ bold: <strong /> }} i18nKey="form.footer.inactiveAccountNote" t={t} />
        </p>
      )}
    </>
  );
});
